import { ApolloLink, Operation } from "@apollo/client"
import { setContext } from "@apollo/client/link/context"
import { PW_AUTH_TOKEN_KEY } from "@pathwright/auth/src/api/client"
import { getIsLocalHost } from "../lib/env"

/** The auth token the operation was sent with, read after the auth link ran. */
export const getSentAuthToken = (operation: Operation): string | null =>
  operation
    .getContext()
    .headers?.authorization?.replace(/^(Bearer|Token) +/, "") ?? null

// Lets the GraphQL server reissue a stored token scoped to another school,
// including a deployed server reached from a local client.
export const developmentClientLink = setContext((_, { headers }) =>
  getIsLocalHost() || process.env.REACT_APP_FEATURES_SANDBOX === "1"
    ? { headers: { ...headers, "X-Pathwright-Development-Client": "1" } }
    : { headers }
)

// Store the token the server reissued for this school, or drop the stored
// token when it could not be reissued. The response already resolved for the
// request school, so this is quiet and the operation is not retried.
export const refreshedAuthTokenLink = new ApolloLink((operation, forward) =>
  forward(operation).map((result) => {
    const refreshedAuthToken = result.extensions?.refreshedAuthToken
    if (refreshedAuthToken === undefined) return result

    // Apply the server's token update only if stored credentials still match
    // those used by this request. Cache isolation does not isolate localStorage.
    const sentToken = getSentAuthToken(operation)
    if (!sentToken || sentToken !== localStorage.getItem(PW_AUTH_TOKEN_KEY))
      return result

    if (typeof refreshedAuthToken === "string") {
      localStorage.setItem(PW_AUTH_TOKEN_KEY, refreshedAuthToken)
    } else if (refreshedAuthToken === null) {
      localStorage.removeItem(PW_AUTH_TOKEN_KEY)
    }
    return result
  })
)
